import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useNavigate } from 'react-router-dom'
import { useState } from 'react'
import { useAuthStore } from './authStore'
import { BrandMark } from '../../components/BrandMark'
import { loginErrorMessage } from './loginErrorMessage'

const schema = z.object({
  email: z.string().email('Введите корректный email'),
  password: z.string().min(1, 'Введите пароль'),
})

type FormValues = z.infer<typeof schema>

/**
 * Вход в CRM — layout LOGIN.B: слева брендовая панель, справа форма.
 * На мобильных (<lg) панель скрывается, остаётся только форма.
 *
 * Набор полей оставлен как был: email + пароль. Без OAuth, без
 * «Запомнить меня», без восстановления пароля — по просьбе Сергея.
 */
export function LoginPage() {
  const navigate = useNavigate()
  const login = useAuthStore((s) => s.login)
  const [error, setError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) })

  const onSubmit = async (values: FormValues) => {
    setError(null)
    try {
      await login(values.email, values.password)
      void navigate('/dashboard', { replace: true })
    } catch (e) {
      setError(loginErrorMessage(e))
    }
  }

  return (
    <div className="min-h-screen flex bg-gray-50">
      {/* Брендовая панель (≥ lg) */}
      <aside className="hidden lg:flex flex-col justify-between w-1/2 xl:w-2/5 bg-brand-600 text-white p-12">
        <div className="flex items-center gap-3">
          <BrandMark size={40} />
          <span className="text-xl font-semibold tracking-tight">micro·hub</span>
        </div>

        <div className="space-y-4">
          <h1 className="text-4xl font-semibold leading-tight">
            Заявки между отделами — в одном окне.
          </h1>
          <p className="text-base text-white/80 max-w-md">
            Внутренний сервис для работы заявок между отделами продаж и закупки.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {['REQUESTS', 'PROCUREMENT', 'BRANDS', 'KP'].map((chip) => (
            <span
              key={chip}
              className="px-3 py-1 bg-white/15 text-white/90 text-xs font-mono uppercase rounded-full"
            >
              {chip}
            </span>
          ))}
        </div>
      </aside>

      {/* Форма */}
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          {/* Знак показываем в форме тоже — на mobile, где аside скрыт */}
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <BrandMark size={36} />
            <span className="text-lg font-semibold text-gray-900">micro·hub</span>
          </div>

          <h2 className="text-2xl font-semibold text-gray-900 mb-1">С возвращением</h2>
          <p className="text-sm text-gray-500 mb-6">Войдите в свой аккаунт.</p>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="email">
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="admin@micro-hub.ru"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                {...register('email')}
              />
              {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="password">
                Пароль
              </label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                placeholder="••••••••"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                {...register('password')}
              />
              {errors.password && (
                <p className="mt-1 text-xs text-red-600">{errors.password.message}</p>
              )}
            </div>

            {error && (
              <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-2 px-4 bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
            >
              {isSubmitting ? 'Вход...' : 'Войти'}
            </button>
          </form>

          <p className="mt-6 text-xs text-center text-gray-400">
            Нет доступа? Обратитесь к администратору.
          </p>
        </div>
      </main>
    </div>
  )
}
